import Phaser from 'phaser';
import {
  GAME_FONT_FAMILY,
  GOLD_TEXT_COLOR,
  ITEM_RARITY_COLORS,
  ITEM_RARITY_NAMES,
} from '@shared/constants';
import type {
  MarketplaceOpenedMessage,
  MarketplaceResultsMessage,
  MarketplaceAppraisalMessage,
  InventorySlotData,
} from '@shared/types/messages';
import type {
  MarketListingView,
  MarketplaceFilters,
  MarketplaceSort,
  AffixOption,
  AffixFilter,
  AppraisalResult,
  RolledAffixWire,
  SavedSearchView,
  MarketAlertView,
  TraderStats,
} from '@shared/types/market';
import { getItemTemplate } from '@shared/data/items';
import { createUIIcon } from '../../sprites/IconAtlases';

/**
 * MarketplacePanel — UI del MARKETPLACE (ítems rolled, modelo PoE Trade).
 *
 * Tabs superiores Intercambio/Bazar (alterna con el ExchangePanel). Dentro del
 * Bazar, sub-tabs Buscar (filtros avanzados por afijo/rareza/slot/precio + grid
 * de resultados con afijos coloreados por tier + comparador) y Vender
 * (publicación con tasación automática + mis publicaciones).
 *
 * Ver docs/ECONOMY_EXCHANGE_MARKETPLACE.md.
 */

export interface MarketplacePanelCallbacks {
  onSearch: (filters: MarketplaceFilters) => void;
  onPublish: (slotIndex: number, price: number) => void;
  onBuy: (listingId: string) => void;
  onCancel: (listingId: string) => void;
  onAppraise: (slotIndex: number) => void;
  onSaveSearch: (name: string, filters: MarketplaceFilters, alert: boolean) => void;
  onDeleteSearch: (id: string) => void;
  onFindDeals: (filters: MarketplaceFilters) => void;
  onAlertsSeen: () => void;
  onRequestStats: () => void;
  onSwitchToExchange: () => void;
  onClose?: () => void;
  onMove?: (x: number, y: number) => void;
}

export interface MarketplacePanelOptions {
  initialX: number;
  initialY: number;
}

const WIDTH = 860;
const HEIGHT = 540;
const HEADER_H = 38;
const DEPTH_BG = 1200;

const COL_FILTERS_W = 240;

const C_PANEL = 0x16130b;
const C_HEADER = 0x261d0e;
const C_ACCENT = 0xc79a3e;
const C_CARD = 0x1d1810;
const C_CARD_SEL = 0x322813;
const T_DIM = '#8a7e54';
const T_LABEL = '#9a8a5a';
const T_BODY = '#e8dcb0';
// Colores de tier (de docs/AFFIXES_AND_TIERS.md)
const TIER_COLORS = ['#aaaaaa', '#aaaaaa', '#e0c050', '#ffd060'];

function font<T extends Phaser.Types.GameObjects.Text.TextStyle>(style?: T): T {
  return { fontFamily: GAME_FONT_FAMILY, resolution: 2, ...style } as T;
}

type SubTab = 'search' | 'sell' | 'wallet';

export class MarketplacePanel {
  private scene: Phaser.Scene;
  private callbacks: MarketplacePanelCallbacks;
  private container: Phaser.GameObjects.Container;
  private isOpen = false;
  private npcId: string | null = null;

  private x: number;
  private y: number;

  // Vocabulario
  private affixOptions: AffixOption[] = [];
  private affixByKey: Map<string, AffixOption> = new Map();
  private subtypes: string[] = [];
  private slots: string[] = [];
  private gold = 0;

  // Inventario propio (para Vender) + equipados (para comparar)
  private inventory: InventorySlotData[] = [];

  // Estado de filtros
  private fRarities: Set<number> = new Set();
  private fSlotIdx = -1; // -1 = todos
  private fSubtypeIdx = -1;
  private fAffixFilters: AffixFilter[] = [];
  private fPriceMin = 0;
  private fPriceMax = 0;
  private fSort: MarketplaceSort = 'recent';

  // Resultados
  private listings: MarketListingView[] = [];
  private total = 0;
  private comparingId: string | null = null;
  private dealsMode = false;

  // Comerciante
  private savedSearches: SavedSearchView[] = [];
  private alertUnseen = 0;
  private saveAsAlert = false;
  private traderStats: TraderStats | null = null;

  // Vender
  private subTab: SubTab = 'search';
  private sellSlotIndex: number | null = null;
  private sellPrice = 100;
  private appraisal: AppraisalResult | null = null;

  // Drag
  private dragging = false;
  private dragOffsetX = 0;
  private dragOffsetY = 0;

  private dynamicObjects: Phaser.GameObjects.GameObject[] = [];
  private goldText!: Phaser.GameObjects.Text;

  constructor(scene: Phaser.Scene, callbacks: MarketplacePanelCallbacks, options: MarketplacePanelOptions) {
    this.scene = scene;
    this.callbacks = callbacks;
    this.x = options.initialX;
    this.y = options.initialY;
    this.container = scene.add.container(this.x, this.y).setDepth(DEPTH_BG).setVisible(false);
    this.container.setScrollFactor(0);
    this.buildChrome();
  }

  // ===========================================================================
  // API pública
  // ===========================================================================

  public open(message: MarketplaceOpenedMessage, gold: number, inventory?: InventorySlotData[]): void {
    this.npcId = message.npcId;
    this.affixOptions = message.affixOptions;
    this.affixByKey = new Map(message.affixOptions.map((a) => [a.modifierKey, a]));
    this.subtypes = message.subtypes;
    this.slots = message.slots;
    this.gold = gold;
    if (inventory) this.inventory = inventory;
    this.isOpen = true;
    this.container.setVisible(true);
    this.render();
    this.callbacks.onSearch(this.buildFilters());
  }

  public isOpenPanel(): boolean {
    return this.isOpen;
  }

  public getCurrentNpcId(): string | null {
    return this.npcId;
  }

  public close(silent = false): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.dragging = false;
    this.container.setVisible(false);
    if (!silent) this.callbacks.onClose?.();
  }

  public setGold(gold: number): void {
    this.gold = gold;
    if (this.goldText) this.goldText.setText(formatGold(gold));
  }

  public setInventory(inventory: InventorySlotData[]): void {
    this.inventory = inventory;
    if (this.isOpen) this.render();
  }

  public applyResults(message: MarketplaceResultsMessage): void {
    this.listings = message.listings;
    this.total = message.total;
    if (this.isOpen) this.render();
  }

  public applyAppraisal(message: MarketplaceAppraisalMessage): void {
    if (message.slotIndex !== this.sellSlotIndex) return;
    this.appraisal = message.result;
    if (message.result?.median != null) this.sellPrice = message.result.median;
    if (this.isOpen) this.render();
  }

  public applySavedSearches(searches: SavedSearchView[]): void {
    this.savedSearches = searches;
    if (this.isOpen) this.render();
  }

  public applyAlerts(alerts: MarketAlertView[]): void {
    this.alertUnseen = alerts.filter((a) => !a.seen).length;
    if (this.isOpen) this.render();
  }

  public applyTraderStats(stats: TraderStats): void {
    this.traderStats = stats;
    if (this.isOpen) this.render();
  }

  public isDraggingPanel(): boolean {
    return this.dragging;
  }

  public isPointerOver(pointer: Phaser.Input.Pointer): boolean {
    if (!this.isOpen) return false;
    return pointer.x >= this.x && pointer.x <= this.x + WIDTH && pointer.y >= this.y && pointer.y <= this.y + HEIGHT;
  }

  public handlePointerMove(pointer: Phaser.Input.Pointer): void {
    if (!this.dragging) return;
    this.x = pointer.x - this.dragOffsetX;
    this.y = pointer.y - this.dragOffsetY;
    this.container.setPosition(this.x, this.y);
  }

  public handlePointerUp(_pointer: Phaser.Input.Pointer): void {
    if (this.dragging) {
      this.dragging = false;
      this.callbacks.onMove?.(this.x, this.y);
    }
  }

  public destroy(): void {
    this.container.destroy(true);
  }

  // ===========================================================================
  // Chrome
  // ===========================================================================

  private buildChrome(): void {
    const bg = this.scene.add.rectangle(0, 0, WIDTH, HEIGHT, C_PANEL, 0.98).setOrigin(0, 0).setStrokeStyle(2, C_ACCENT, 1);
    this.container.add(bg);
    const header = this.scene.add.rectangle(0, 0, WIDTH, HEADER_H, C_HEADER, 1).setOrigin(0, 0).setStrokeStyle(1, C_ACCENT, 0.7);
    this.container.add(header);
    this.container.add(this.scene.add.rectangle(0, HEADER_H, WIDTH, 2, C_ACCENT, 0.9).setOrigin(0, 0));

    // Tabs Intercambio / Bazar
    this.addTabButton(12, 'Intercambio', false, () => this.callbacks.onSwitchToExchange());
    this.addTabButton(120, 'Bazar', true, () => {});

    const coin = createUIIcon(this.scene, WIDTH - 170, HEADER_H / 2, 'gold', 16);
    if (coin) this.container.add(coin);
    this.goldText = this.scene.add
      .text(WIDTH - 158, HEADER_H / 2, formatGold(this.gold), font({ fontSize: '14px', color: GOLD_TEXT_COLOR, fontStyle: 'bold' }))
      .setOrigin(0, 0.5);
    this.container.add(this.goldText);

    const closeBtn = this.scene.add
      .text(WIDTH - 22, HEADER_H / 2, '✕', font({ fontSize: '18px', color: '#e0b0b0' }))
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    closeBtn.on('pointerdown', () => this.close());
    this.container.add(closeBtn);

    const headerHit = this.scene.add
      .rectangle(220, 0, WIDTH - 260, HEADER_H, 0xffffff, 0.001)
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true });
    headerHit.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.dragging = true;
      this.dragOffsetX = pointer.x - this.x;
      this.dragOffsetY = pointer.y - this.y;
    });
    this.container.add(headerHit);
  }

  private addTabButton(x: number, label: string, active: boolean, onClick: () => void): void {
    const w = 100;
    const rect = this.scene.add
      .rectangle(x, 6, w, HEADER_H - 10, active ? 0x3a2e14 : 0x201a0d, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(1, active ? C_ACCENT : 0x4a3c1c, 1)
      .setInteractive({ useHandCursor: true });
    rect.on('pointerdown', onClick);
    this.container.add(rect);
    this.container.add(
      this.scene.add
        .text(x + w / 2, HEADER_H / 2, label, font({ fontSize: '13px', color: active ? '#f4dc92' : '#9a8e60', fontStyle: active ? 'bold' : 'normal' }))
        .setOrigin(0.5)
    );
  }

  // ===========================================================================
  // Render
  // ===========================================================================

  private clearDynamic(): void {
    for (const o of this.dynamicObjects) o.destroy();
    this.dynamicObjects = [];
  }

  private addDyn<T extends Phaser.GameObjects.GameObject>(obj: T): T {
    this.container.add(obj);
    this.dynamicObjects.push(obj);
    return obj;
  }

  private txt(x: number, y: number, s: string, style: Phaser.Types.GameObjects.Text.TextStyle): Phaser.GameObjects.Text {
    return this.addDyn(this.scene.add.text(x, y, s, font(style)).setOrigin(0, 0));
  }

  private itemIcon(x: number, y: number, iconName: string, size: number): void {
    const key = `item_icon_${iconName}`;
    const tex = this.scene.textures.exists(key) ? key : 'item_icon_noicon';
    if (!this.scene.textures.exists(tex)) return;
    this.addDyn(this.scene.add.image(x, y, tex).setOrigin(0.5).setDisplaySize(size, size));
  }

  private coin(x: number, y: number, size = 12): void {
    const c = createUIIcon(this.scene, x, y, 'gold', size);
    if (c) this.addDyn(c);
  }

  private render(): void {
    this.clearDynamic();
    this.goldText.setText(formatGold(this.gold));
    // Campana de alertas (si hay no vistas)
    if (this.alertUnseen > 0) {
      const bell = this.addDyn(
        this.scene.add
          .text(WIDTH - 228, HEADER_H / 2, `🔔 ${this.alertUnseen}`, font({ fontSize: '13px', color: '#ffd060', fontStyle: 'bold' }))
          .setOrigin(0, 0.5)
          .setInteractive({ useHandCursor: true })
      );
      bell.on('pointerdown', () => this.callbacks.onAlertsSeen());
    }
    // Sub-tabs
    this.addSubTab(COL_FILTERS_W + 12, 'Buscar', this.subTab === 'search', () => {
      this.subTab = 'search';
      this.render();
    });
    this.addSubTab(COL_FILTERS_W + 92, 'Vender', this.subTab === 'sell', () => {
      this.subTab = 'sell';
      this.render();
    });
    this.addSubTab(COL_FILTERS_W + 172, 'Cartera', this.subTab === 'wallet', () => {
      this.subTab = 'wallet';
      this.callbacks.onRequestStats();
      this.render();
    });

    this.renderFilters();
    if (this.subTab === 'search') this.renderResults();
    else if (this.subTab === 'sell') this.renderSell();
    else this.renderWallet();
  }

  private renderWallet(): void {
    const x0 = COL_FILTERS_W + 12;
    const colW = WIDTH - x0 - 14;
    let y = HEADER_H + 36;
    this.txt(x0, y, 'CARTERA DEL COMERCIANTE', { fontSize: '11px', color: T_LABEL, fontStyle: 'bold' });
    y += 20;

    const s = this.traderStats;
    if (!s) {
      this.txt(x0, y, 'Cargando…', { fontSize: '12px', color: T_DIM });
      return;
    }
    if (s.tradeCount === 0) {
      this.txt(x0, y, 'Todavía no operaste en el mercado.', { fontSize: '12px', color: '#6a6044' });
      return;
    }

    // Resumen (dos columnas)
    const col2 = x0 + colW / 2;
    const stat = (sx: number, sy: number, label: string, value: string, color: string): void => {
      this.txt(sx, sy, label, { fontSize: '10px', color: T_DIM });
      this.coin(sx + 2, sy + 22, 12);
      this.txt(sx + 12, sy + 15, value, { fontSize: '14px', color, fontStyle: 'bold' });
    };
    stat(x0, y, 'Gastado (compras)', formatGold(s.goldSpent), '#e0a0a0');
    stat(col2, y, 'Ganado neto (ventas)', formatGold(s.goldEarned), '#a0e0a0');
    y += 40;
    const netColor = s.netFlow >= 0 ? '#8ad98a' : '#e06a6a';
    stat(x0, y, 'Flujo neto', `${s.netFlow >= 0 ? '+' : ''}${formatGold(s.netFlow)}`, netColor);
    stat(col2, y, 'Fees pagados', formatGold(s.feesPaid), GOLD_TEXT_COLOR);
    y += 40;
    this.txt(x0, y, `Operaciones: ${s.tradeCount}   ·   Volumen movido: ${formatGold(s.volumeGold)} oro`, { fontSize: '11px', color: '#cdbf8c' });
    y += 24;

    // P&L por ítem
    this.txt(x0, y, 'POR ÍTEM (flujo neto)', { fontSize: '11px', color: T_LABEL, fontStyle: 'bold' });
    y += 16;
    for (const it of s.perItem.slice(0, 5)) {
      const flowColor = it.netFlow >= 0 ? '#8ad98a' : '#e0a0a0';
      this.txt(x0, y, it.itemName, { fontSize: '11px', color: T_BODY });
      this.txt(x0 + 150, y, `C ${it.boughtQty} / V ${it.soldQty}`, { fontSize: '10px', color: T_DIM });
      this.txt(x0 + colW - 90, y, `${it.netFlow >= 0 ? '+' : ''}${formatGold(it.netFlow)}`, { fontSize: '11px', color: flowColor });
      y += 16;
    }
    y += 12;

    // Historial reciente
    this.txt(x0, y, 'HISTORIAL RECIENTE', { fontSize: '11px', color: T_LABEL, fontStyle: 'bold' });
    y += 16;
    for (const t of s.recent.slice(0, 8)) {
      const buy = t.side === 'buy';
      this.addDyn(this.scene.add.rectangle(x0, y, 14, 14, buy ? 0x46c46a : 0xe06a6a, 1).setOrigin(0, 0));
      this.txt(x0 + 3, y + 1, buy ? 'C' : 'V', { fontSize: '10px', color: '#101010', fontStyle: 'bold' });
      this.txt(x0 + 20, y + 1, `${t.qty}× ${t.itemName}`, { fontSize: '11px', color: T_BODY });
      this.coin(x0 + colW - 96, y + 8, 10);
      this.txt(x0 + colW - 88, y + 1, `${formatGold(t.price)}${t.fee > 0 ? ` (−${t.fee})` : ''}`, { fontSize: '11px', color: GOLD_TEXT_COLOR });
      y += 16;
    }
  }

  private addSubTab(x: number, label: string, active: boolean, onClick: () => void): void {
    const w = 74;
    const y = HEADER_H + 8;
    const rect = this.addDyn(
      this.scene.add
        .rectangle(x, y, w, 22, active ? 0x3a2e14 : 0x201a0d, 1)
        .setOrigin(0, 0)
        .setStrokeStyle(1, active ? C_ACCENT : 0x4a3c1c, 1)
        .setInteractive({ useHandCursor: true })
    );
    rect.on('pointerdown', onClick);
    this.addDyn(
      this.scene.add
        .text(x + w / 2, y + 11, label, font({ fontSize: '12px', color: active ? '#f4dc92' : '#9a8e60', fontStyle: active ? 'bold' : 'normal' }))
        .setOrigin(0.5)
    );
  }

  // ----- Filtros -----

  private renderFilters(): void {
    const x0 = 12;
    let y = HEADER_H + 12;
    this.txt(x0, y, 'FILTROS', { fontSize: '11px', color: T_LABEL, fontStyle: 'bold' });
    y += 20;

    // Rareza chips
    this.txt(x0, y, 'Rareza', { fontSize: '11px', color: '#bfae78' });
    y += 16;
    const rarities = [1, 2, 3, 4]; // Raro, Épico, Legendario, Set (Común rara vez se publica)
    let rx = x0;
    for (const r of rarities) {
      const on = this.fRarities.has(r);
      const colorHex = `#${ITEM_RARITY_COLORS[r as 1].toString(16).padStart(6, '0')}`;
      const chip = this.addDyn(
        this.scene.add
          .rectangle(rx, y, 50, 20, on ? 0x3a2e14 : 0x1a150c, 1)
          .setOrigin(0, 0)
          .setStrokeStyle(1, on ? C_ACCENT : 0x4a3c1c, 1)
          .setInteractive({ useHandCursor: true })
      );
      chip.on('pointerdown', () => {
        if (on) this.fRarities.delete(r);
        else this.fRarities.add(r);
        this.render();
      });
      this.addDyn(
        this.scene.add
          .text(rx + 25, y + 10, (ITEM_RARITY_NAMES[r as 1] ?? '').slice(0, 4), font({ fontSize: '10px', color: on ? colorHex : '#7a6e44' }))
          .setOrigin(0.5)
      );
      rx += 54;
      if (rx > x0 + 160) {
        rx = x0;
        y += 24;
      }
    }
    y += 28;

    // Slot cycle
    y = this.renderCycle(x0, y, 'Slot', this.fSlotIdx, this.slots, (i) => {
      this.fSlotIdx = i;
      this.render();
    });
    // Subtype cycle
    y = this.renderCycle(x0, y, 'Subtipo', this.fSubtypeIdx, this.subtypes, (i) => {
      this.fSubtypeIdx = i;
      this.render();
    });

    // Afijos
    this.txt(x0, y, 'Afijos (mínimo)', { fontSize: '11px', color: '#bfae78' });
    y += 16;
    for (let i = 0; i < this.fAffixFilters.length; i += 1) {
      const af = this.fAffixFilters[i];
      const opt = this.affixByKey.get(af.modifierKey);
      const isPct = opt?.displayFormat === 'percent';
      const shown = isPct ? `${Math.round(af.min * 100)}%` : String(af.min);
      this.txt(x0, y + 2, `${opt?.displayName ?? af.modifierKey} ≥ ${shown}`, { fontSize: '11px', color: T_BODY });
      // − / +
      this.miniBtn(x0 + 150, y, '−', () => {
        af.min = Math.max(0, af.min - (isPct ? 0.01 : 1));
        this.render();
      });
      this.miniBtn(x0 + 172, y, '+', () => {
        af.min = af.min + (isPct ? 0.01 : 1);
        this.render();
      });
      this.miniBtn(x0 + 200, y, '✕', () => {
        this.fAffixFilters.splice(i, 1);
        this.render();
      });
      y += 22;
    }
    if (this.fAffixFilters.length < 4) {
      const add = this.addDyn(
        this.scene.add
          .text(x0, y, '+ añadir afijo', font({ fontSize: '11px', color: '#8ad98a' }))
          .setOrigin(0, 0)
          .setInteractive({ useHandCursor: true })
      );
      add.on('pointerdown', () => this.cycleAddAffix());
      y += 22;
    }

    // Precio
    y += 4;
    this.txt(x0, y, 'Precio', { fontSize: '11px', color: '#bfae78' });
    y += 16;
    this.txt(x0, y + 4, `mín`, { fontSize: '10px', color: T_DIM });
    this.miniBtn(x0 + 28, y, '−', () => {
      this.fPriceMin = Math.max(0, this.fPriceMin - 50);
      this.render();
    });
    this.txt(x0 + 52, y + 3, String(this.fPriceMin), { fontSize: '11px', color: T_BODY });
    this.miniBtn(x0 + 100, y, '+', () => {
      this.fPriceMin += 50;
      this.render();
    });
    this.txt(x0 + 130, y + 4, `máx`, { fontSize: '10px', color: T_DIM });
    this.miniBtn(x0 + 158, y, '−', () => {
      this.fPriceMax = Math.max(0, this.fPriceMax - 50);
      this.render();
    });
    this.txt(x0 + 182, y + 3, this.fPriceMax === 0 ? '∞' : String(this.fPriceMax), { fontSize: '11px', color: T_BODY });
    this.miniBtn(x0 + 210, y, '+', () => {
      this.fPriceMax += 50;
      this.render();
    });
    y += 26;

    // Sort cycle
    const sorts: MarketplaceSort[] = ['recent', 'price_asc', 'price_desc', 'score_desc'];
    const sortLabels: Record<MarketplaceSort, string> = {
      recent: 'Recientes',
      price_asc: 'Precio ↑',
      price_desc: 'Precio ↓',
      score_desc: 'Valor ↓',
    };
    const sortBtn = this.addDyn(
      this.scene.add
        .rectangle(x0, y, 218, 24, 0x241d10, 1)
        .setOrigin(0, 0)
        .setStrokeStyle(1, 0x4a3c1c, 1)
        .setInteractive({ useHandCursor: true })
    );
    sortBtn.on('pointerdown', () => {
      const idx = sorts.indexOf(this.fSort);
      this.fSort = sorts[(idx + 1) % sorts.length];
      this.render();
    });
    this.txt(x0 + 8, y + 6, `Orden: ${sortLabels[this.fSort]}`, { fontSize: '11px', color: '#d8c890' });
    y += 30;

    // Buscar + Ofertas (Deal Finder)
    this.bigBtn(x0, y, 104, 28, 'BUSCAR', 0x2e5a3a, () => {
      this.dealsMode = false;
      this.callbacks.onSearch(this.buildFilters());
    });
    this.bigBtn(x0 + 114, y, 104, 28, '★ OFERTAS', 0x5a4a1e, () => {
      this.dealsMode = true;
      this.callbacks.onFindDeals(this.buildFilters());
    });
    y += 36;

    // Guardar búsqueda + toggle de alerta
    const alertColor = this.saveAsAlert ? '#e6c860' : '#7a6e44';
    const bell = this.addDyn(
      this.scene.add
        .rectangle(x0, y, 24, 24, this.saveAsAlert ? 0x3a2e14 : 0x1a150c, 1)
        .setOrigin(0, 0)
        .setStrokeStyle(1, this.saveAsAlert ? C_ACCENT : 0x4a3c1c, 1)
        .setInteractive({ useHandCursor: true })
    );
    bell.on('pointerdown', () => {
      this.saveAsAlert = !this.saveAsAlert;
      this.render();
    });
    this.addDyn(this.scene.add.text(x0 + 12, y + 12, '🔔', font({ fontSize: '12px', color: alertColor })).setOrigin(0.5));
    this.bigBtn(x0 + 30, y, 188, 24, this.saveAsAlert ? 'GUARDAR + ALERTAR' : 'GUARDAR BÚSQUEDA', 0x2a3a4a, () => {
      const n = this.savedSearches.length + 1;
      this.callbacks.onSaveSearch(`Búsqueda ${n}`, this.buildFilters(), this.saveAsAlert);
    });
    y += 30;

    // Lista de búsquedas guardadas
    for (const s of this.savedSearches.slice(0, 4)) {
      const apply = this.addDyn(
        this.scene.add
          .rectangle(x0, y, 196, 20, 0x1d1810, 1)
          .setOrigin(0, 0)
          .setStrokeStyle(1, 0x4a3c1c, 1)
          .setInteractive({ useHandCursor: true })
      );
      apply.on('pointerdown', () => {
        this.applySavedFilters(s.filters);
        this.dealsMode = false;
        this.callbacks.onSearch(s.filters);
      });
      this.addDyn(
        this.scene.add.text(x0 + 6, y + 10, `${s.alert ? '🔔 ' : ''}${s.name}`, font({ fontSize: '11px', color: '#d8cda0' })).setOrigin(0, 0.5)
      );
      const del = this.txt(x0 + 200, y + 4, '✕', { fontSize: '12px', color: '#e09090' });
      del.setInteractive({ useHandCursor: true });
      del.on('pointerdown', () => this.callbacks.onDeleteSearch(s.id));
      y += 24;
    }
  }

  /** Aplica los filtros de una búsqueda guardada al estado del panel. */
  private applySavedFilters(f: MarketplaceFilters): void {
    this.fSlotIdx = f.slot ? this.slots.indexOf(f.slot) : -1;
    this.fSubtypeIdx = f.subtype ? this.subtypes.indexOf(f.subtype) : -1;
    this.fRarities = new Set(f.rarities ?? []);
    this.fAffixFilters = (f.affixMins ?? []).map((a) => ({ ...a }));
    this.fPriceMin = f.priceMin ?? 0;
    this.fPriceMax = f.priceMax ?? 0;
    this.fSort = f.sort ?? 'recent';
  }

  private renderCycle(
    x0: number,
    y: number,
    label: string,
    currentIdx: number,
    options: string[],
    onChange: (idx: number) => void
  ): number {
    this.txt(x0, y, label, { fontSize: '11px', color: '#bfae78' });
    const current = currentIdx < 0 ? 'Todos' : translateToken(options[currentIdx] ?? '');
    const btn = this.addDyn(
      this.scene.add
        .rectangle(x0 + 60, y - 2, 158, 20, 0x241d10, 1)
        .setOrigin(0, 0)
        .setStrokeStyle(1, 0x4a3c1c, 1)
        .setInteractive({ useHandCursor: true })
    );
    btn.on('pointerdown', () => {
      // ciclo: -1 (todos) → 0 → 1 … → -1
      const next = currentIdx + 1 >= options.length ? -1 : currentIdx + 1;
      onChange(next);
    });
    this.txt(x0 + 66, y + 2, current, { fontSize: '11px', color: '#d8c890' });
    return y + 26;
  }

  private cycleAddAffix(): void {
    // Agrega el primer afijo no usado todavía.
    const used = new Set(this.fAffixFilters.map((f) => f.modifierKey));
    const next = this.affixOptions.find((o) => !used.has(o.modifierKey));
    if (!next) return;
    this.fAffixFilters.push({ modifierKey: next.modifierKey, min: next.displayFormat === 'percent' ? 0.01 : 1 });
    this.render();
  }

  private buildFilters(): MarketplaceFilters {
    return {
      slot: this.fSlotIdx >= 0 ? this.slots[this.fSlotIdx] : undefined,
      subtype: this.fSubtypeIdx >= 0 ? this.subtypes[this.fSubtypeIdx] : undefined,
      rarities: this.fRarities.size > 0 ? [...this.fRarities] : undefined,
      affixMins: this.fAffixFilters.length > 0 ? this.fAffixFilters.map((f) => ({ ...f })) : undefined,
      priceMin: this.fPriceMin > 0 ? this.fPriceMin : undefined,
      priceMax: this.fPriceMax > 0 ? this.fPriceMax : undefined,
      sort: this.fSort,
    };
  }

  // ----- Resultados -----

  private renderResults(): void {
    const x0 = COL_FILTERS_W + 12;
    const colW = WIDTH - x0 - 14;
    let y = HEADER_H + 36;
    this.txt(x0, y, this.dealsMode ? `★ ${this.total} ofertas (bajo valor de mercado)` : `${this.total} resultados`, {
      fontSize: '11px',
      color: this.dealsMode ? '#e6c860' : T_LABEL,
      fontStyle: this.dealsMode ? 'bold' : 'normal',
    });
    y += 18;

    if (this.listings.length === 0) {
      this.txt(x0, y, this.dealsMode ? 'No hay ofertas ahora mismo.' : 'No hay listados que coincidan con tus filtros.', {
        fontSize: '12px',
        color: '#6a6044',
      });
      return;
    }

    for (const l of this.listings.slice(0, 7)) {
      const cardH = this.comparingId === l.id ? 96 : 56;
      const rarityHex = `#${(ITEM_RARITY_COLORS[l.rolledRarity as 1] ?? 0xffffff).toString(16).padStart(6, '0')}`;
      const card = this.addDyn(
        this.scene.add.rectangle(x0, y, colW, cardH, C_CARD, 1).setOrigin(0, 0).setStrokeStyle(1, 0x4a3c1c, 1)
      );
      card.setData('noop', true);
      this.itemIcon(x0 + 22, y + 22, l.itemIcon, 34);
      this.txt(x0 + 46, y + 6, l.itemName, { fontSize: '13px', color: rarityHex, fontStyle: 'bold' });
      // afijos en una línea
      this.renderAffixLine(x0 + 46, y + 24, l.rolledAffixes);
      // precio
      this.coin(x0 + colW - 120, y + 14, 13);
      this.txt(x0 + colW - 110, y + 7, formatGold(l.price), { fontSize: '14px', color: GOLD_TEXT_COLOR, fontStyle: 'bold' });
      // badge de descuento (Deal Finder)
      if (l.dealDiscount != null && l.dealDiscount > 0) {
        const pct = Math.round(l.dealDiscount * 100);
        this.addDyn(this.scene.add.rectangle(x0 + colW - 120, y + 26, 44, 14, 0x2e6a38, 1).setOrigin(0, 0).setStrokeStyle(1, 0x7ad07a, 1));
        this.addDyn(this.scene.add.text(x0 + colW - 98, y + 33, `−${pct}%`, font({ fontSize: '10px', color: '#d6ffd6', fontStyle: 'bold' })).setOrigin(0.5));
      }
      // botones
      if (l.isMine) {
        this.smallBtn(x0 + colW - 70, y + 30, 'Cancelar', '#e09090', () => {
          this.callbacks.onCancel(l.id);
          this.callbacks.onSearch(this.buildFilters());
        });
      } else {
        this.smallBtn(x0 + colW - 130, y + 30, 'Comparar', '#cdbf8c', () => {
          this.comparingId = this.comparingId === l.id ? null : l.id;
          this.render();
        });
        this.smallBtn(x0 + colW - 60, y + 30, 'Comprar', '#8ad98a', () => {
          this.callbacks.onBuy(l.id);
          this.callbacks.onSearch(this.buildFilters());
        });
      }
      // comparador expandido
      if (this.comparingId === l.id) {
        this.renderComparator(x0 + 46, y + 44, colW - 60, l);
      }
      y += cardH + 6;
    }
  }

  private renderAffixLine(x: number, y: number, affixes: RolledAffixWire): void {
    let cx = x;
    const entries = Object.entries(affixes);
    if (entries.length === 0) {
      this.txt(cx, y, 'sin afijos', { fontSize: '10px', color: T_DIM });
      return;
    }
    for (const [key, comps] of entries) {
      const opt = this.affixByKey.get(key);
      const isPct = opt?.displayFormat === 'percent';
      const sum = comps.reduce((s, c) => s + c.v, 0);
      const bestTier = comps.reduce((m, c) => Math.max(m, c.t), 1);
      const shown = isPct ? `+${Math.round(sum * 100)}% ${opt?.displayName ?? key}` : `+${sum} ${opt?.displayName ?? key}`;
      const label = this.txt(cx, y, shown, { fontSize: '10px', color: TIER_COLORS[bestTier] ?? '#aaaaaa' });
      cx += label.width + 10;
      if (cx > x + 360) break;
    }
  }

  /** Comparador: afijos del listado vs los del item equipado en ese slot. */
  private renderComparator(x: number, y: number, w: number, l: MarketListingView): void {
    const equipped = this.inventory.find((s) => s.equippedIn && s.equippedIn === l.slot);
    if (!equipped) {
      this.txt(x, y, 'No tenés nada equipado en ese slot para comparar.', { fontSize: '10px', color: T_DIM });
      return;
    }
    const eqAffixes = equipped.rolledAffixes as RolledAffixWire;
    const keys = new Set([...Object.keys(l.rolledAffixes), ...Object.keys(eqAffixes)]);
    let cx = x;
    for (const key of keys) {
      const opt = this.affixByKey.get(key);
      const isPct = opt?.displayFormat === 'percent';
      const listVal = (l.rolledAffixes[key] ?? []).reduce((s, c) => s + c.v, 0);
      const eqVal = (eqAffixes[key] ?? []).reduce((s, c) => s + c.v, 0);
      const delta = listVal - eqVal;
      const arrow = delta > 0 ? '▲' : delta < 0 ? '▼' : '=';
      const color = delta > 0 ? '#8ad98a' : delta < 0 ? '#e0a0a0' : '#999999';
      const dShown = isPct ? `${Math.round(Math.abs(delta) * 100)}%` : String(Math.abs(delta));
      const label = this.txt(cx, y, `${arrow}${dShown} ${opt?.displayName ?? key}`, { fontSize: '10px', color });
      cx += label.width + 10;
      if (cx > x + w - 60) {
        cx = x;
        y += 14;
      }
    }
  }

  // ----- Vender -----

  private renderSell(): void {
    const x0 = COL_FILTERS_W + 12;
    const colW = WIDTH - x0 - 14;
    let y = HEADER_H + 36;

    this.txt(x0, y, 'PUBLICAR — elegí un item de tu inventario', { fontSize: '11px', color: T_LABEL, fontStyle: 'bold' });
    y += 20;

    // Grid de items listables (no equipados, no fungibles → tienen afijos o son unique/set)
    const listable = this.inventory.filter((s) => !s.equippedIn && this.isListable(s));
    if (listable.length === 0) {
      this.txt(x0, y, 'No tenés items publicables (armas/armaduras con afijos, únicos, sets).', { fontSize: '11px', color: '#6a6044' });
    }
    let gx = x0;
    let gy = y;
    for (const s of listable.slice(0, 12)) {
      const sel = this.sellSlotIndex === s.slotIndex;
      const rarityHex = `#${(ITEM_RARITY_COLORS[s.rolledRarity as 1] ?? 0xffffff).toString(16).padStart(6, '0')}`;
      const cell = this.addDyn(
        this.scene.add
          .rectangle(gx, gy, 40, 40, sel ? C_CARD_SEL : C_CARD, 1)
          .setOrigin(0, 0)
          .setStrokeStyle(sel ? 2 : 1, sel ? C_ACCENT : parseInt(rarityHex.slice(1), 16), 1)
          .setInteractive({ useHandCursor: true })
      );
      cell.on('pointerdown', () => {
        this.sellSlotIndex = s.slotIndex;
        this.appraisal = null;
        this.callbacks.onAppraise(s.slotIndex);
        this.render();
      });
      this.itemIcon(gx + 20, gy + 20, getItemTemplate(s.itemId)?.icon ?? 'noicon', 34);
      gx += 46;
      if (gx > x0 + 46 * 8) {
        gx = x0;
        gy += 46;
      }
    }
    y = gy + 52;

    // Detalle de publicación
    if (this.sellSlotIndex != null) {
      const slot = this.inventory.find((s) => s.slotIndex === this.sellSlotIndex);
      if (slot) {
        const tpl = getItemTemplate(slot.itemId);
        const rarityHex = `#${(ITEM_RARITY_COLORS[slot.rolledRarity as 1] ?? 0xffffff).toString(16).padStart(6, '0')}`;
        this.txt(x0, y, tpl?.name ?? slot.itemId, { fontSize: '13px', color: rarityHex, fontStyle: 'bold' });
        y += 18;
        this.renderAffixLine(x0, y, slot.rolledAffixes as RolledAffixWire);
        y += 20;

        // Tasación
        if (this.appraisal) {
          const a = this.appraisal;
          if (a.sampleCount > 0 && a.median != null) {
            this.txt(x0, y, `Sugerido: ${formatGold(a.suggestedMin ?? 0)}–${formatGold(a.suggestedMax ?? 0)} oro  ·  mediana ${formatGold(a.median)} (${a.sampleCount} ventas)`, { fontSize: '11px', color: '#d8c890' });
          } else {
            this.txt(x0, y, `Sin ventas comparables. Score del item: ${Math.round(a.score * 100)}%`, { fontSize: '11px', color: T_DIM });
          }
          y += 20;
        } else {
          this.txt(x0, y, 'Tasando…', { fontSize: '11px', color: T_DIM });
          y += 20;
        }

        // Precio
        this.txt(x0, y, 'Precio (oro)', { fontSize: '11px', color: '#bfae78' });
        y += 16;
        this.miniBtn(x0, y, '−', () => {
          this.sellPrice = Math.max(1, this.sellPrice - 10);
          this.render();
        });
        this.addDyn(this.scene.add.rectangle(x0 + 26, y, 90, 24, 0x12100a, 1).setOrigin(0, 0).setStrokeStyle(1, 0x4a3c1c, 1));
        this.addDyn(this.scene.add.text(x0 + 71, y + 12, String(this.sellPrice), font({ fontSize: '14px', color: '#ffffff', fontStyle: 'bold' })).setOrigin(0.5));
        this.miniBtn(x0 + 120, y, '+', () => {
          this.sellPrice += 10;
          this.render();
        });
        const fee = Math.max(5, Math.floor(this.sellPrice * 0.01));
        this.txt(x0 + 160, y + 4, `fee ${fee} oro`, { fontSize: '10px', color: T_DIM });
        y += 32;

        this.bigBtn(x0, y, 160, 28, 'PUBLICAR', 0x2e5a3a, () => {
          if (this.sellSlotIndex == null) return;
          this.callbacks.onPublish(this.sellSlotIndex, this.sellPrice);
          this.sellSlotIndex = null;
          this.appraisal = null;
          this.callbacks.onSearch(this.buildFilters());
        });
        y += 40;
      }
    }

    // Mis publicaciones
    const mine = this.listings.filter((l) => l.isMine);
    this.txt(x0, y, `MIS PUBLICACIONES (${mine.length})`, { fontSize: '11px', color: T_LABEL, fontStyle: 'bold' });
    y += 16;
    for (const l of mine.slice(0, 4)) {
      this.itemIcon(x0 + 10, y + 8, l.itemIcon, 18);
      this.txt(x0 + 24, y + 2, `${l.itemName}`, { fontSize: '11px', color: `#${(ITEM_RARITY_COLORS[l.rolledRarity as 1] ?? 0xffffff).toString(16).padStart(6, '0')}` });
      this.coin(x0 + 220, y + 9, 11);
      this.txt(x0 + 228, y + 2, formatGold(l.price), { fontSize: '11px', color: GOLD_TEXT_COLOR });
      this.smallBtn(x0 + 300, y + 2, 'Cancelar', '#e09090', () => this.callbacks.onCancel(l.id));
      y += 20;
    }
  }

  private isListable(s: InventorySlotData): boolean {
    const tpl = getItemTemplate(s.itemId);
    if (!tpl) return false;
    return Boolean(tpl.affixable || tpl.unique || tpl.setId);
  }

  // ----- Botones helper -----

  private miniBtn(x: number, y: number, label: string, onClick: () => void): void {
    const rect = this.addDyn(
      this.scene.add.rectangle(x, y, 20, 20, 0x2a2210, 1).setOrigin(0, 0).setStrokeStyle(1, 0x4a3c1c, 1).setInteractive({ useHandCursor: true })
    );
    rect.on('pointerdown', onClick);
    this.addDyn(this.scene.add.text(x + 10, y + 10, label, font({ fontSize: '12px', color: '#d8c890' })).setOrigin(0.5));
  }

  private smallBtn(x: number, y: number, label: string, color: string, onClick: () => void): void {
    const t = this.txt(x, y, label, { fontSize: '11px', color });
    t.setInteractive({ useHandCursor: true });
    t.on('pointerover', () => t.setColor('#ffffff'));
    t.on('pointerout', () => t.setColor(color));
    t.on('pointerdown', onClick);
  }

  private bigBtn(x: number, y: number, w: number, h: number, label: string, color: number, onClick: () => void): void {
    const rect = this.addDyn(
      this.scene.add.rectangle(x, y, w, h, color, 1).setOrigin(0, 0).setStrokeStyle(1, C_ACCENT, 1).setInteractive({ useHandCursor: true })
    );
    rect.on('pointerdown', onClick);
    this.addDyn(this.scene.add.text(x + w / 2, y + h / 2, label, font({ fontSize: '12px', color: '#f4ecc8', fontStyle: 'bold' })).setOrigin(0.5));
  }
}

// ===========================================================================
// Utilidades
// ===========================================================================

function formatGold(n: number): string {
  return Math.floor(n).toLocaleString('es-AR');
}

/** Traduce tokens de slot/subtipo a etiquetas legibles en español. */
function translateToken(token: string): string {
  const map: Record<string, string> = {
    weapon: 'Arma',
    body: 'Torso',
    head: 'Cabeza',
    shield: 'Escudo',
    ring: 'Anillo',
    amulet: 'Amuleto',
    back: 'Espalda',
    boots: 'Botas',
    sword: 'Espada',
    dagger: 'Daga',
    axe: 'Hacha',
    mace: 'Maza',
    staff: 'Báculo',
    wand: 'Varita',
    knuckles: 'Puños',
    bow: 'Arco',
    crossbow: 'Ballesta',
    throwable: 'Arrojadiza',
    heavy: 'Pesada',
    light: 'Ligera',
    cloth: 'Tela',
  };
  return map[token] ?? token;
}
